// ── Vite entry — bootstraps the members app ──────────────────────────
// 1. Set React/ReactDOM as window globals (existing .jsx files reference
//    React.useState / React.useEffect / React.useRef directly — they don't
//    have ES imports yet, so we expose React through the window).
// 2. Side-effect-import the lib bundle (attaches window.GQ.*).
// 3. Side-effect-import every page .jsx (each attaches window.<Page>).
// 4. Mount <App/>.
//
// Phase 1 migration keeps the window.* contract intact so we don't have to
// touch ~25 component files. Phase ≥2 can incrementally convert files to
// real ES imports.

import React from 'react';
import * as ReactDOM from 'react-dom/client';

window.React    = React;
window.ReactDOM = ReactDOM;

// Lib bootstrap — order matters: stats → data → engine → optimize → stress → state → utils
import './supabase.js';
import './lib/stats.js';
import './lib/data.js';
import './lib/engine.js';
import './lib/optimize.js';
import './lib/stress.js';
import './lib/state.js';
import './lib/utils.js';
import './lib/claude-shim.js';   // defines window.claude.complete if the host doesn't
import './lib/ai-tools.js';

// UI primitives + icons (no Plotly dependency)
import './icons.jsx';
import './ui.jsx';
import './charts.jsx';
import './onboarding.jsx';

// Pages
import './ai-chat.jsx';
import './tearsheet.jsx';
import './vault.jsx';
import './admin.jsx';
import './designer.jsx';
import './optimize.jsx';
import './stress.jsx';
import './marketplace.jsx';
import './settings.jsx';
import './strategy-detail.jsx';
import './mint-strategy.jsx';
import './backtest.jsx';
import './compare.jsx';
import './publish-modal.jsx';

// Root shell — defines <App/> on window
import './app.jsx';

// Mount. Sentry/PostHog chunks are already split by Vite; importing eagerly
// so the ErrorBoundary is available before first render.
import './lib/observability.js';

function ErrorFallback({ error, resetError }) {
  return (
    <div style={{
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      minHeight:'100vh', padding:32, background:'#050505', color:'#f5f5f7',
      fontFamily:"'Inter','system-ui',sans-serif", textAlign:'center',
    }}>
      <div style={{ fontSize:36, marginBottom:16 }}>⚠️</div>
      <h2 style={{ fontSize:20, fontWeight:600, marginBottom:8, color:'#f5f5f7' }}>Something went wrong</h2>
      <p style={{ fontSize:13, color:'rgba(245,245,247,0.5)', maxWidth:420, lineHeight:1.6, marginBottom:20 }}>
        {error?.message || 'An unexpected error occurred.'}
      </p>
      <div style={{ display:'flex', gap:12 }}>
        <button onClick={resetError} style={{
          padding:'8px 20px', borderRadius:9999,
          background:'rgba(217,70,239,0.15)', border:'1px solid rgba(217,70,239,0.3)',
          color:'#D946EF', cursor:'pointer', fontSize:13, fontWeight:500,
        }}>Try again</button>
        <button onClick={()=>window.location.href='auth.html'} style={{
          padding:'8px 20px', borderRadius:9999,
          background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)',
          color:'rgba(245,245,247,0.6)', cursor:'pointer', fontSize:13,
        }}>Sign out</button>
      </div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
const App  = window.App;
// GQ.observability is synchronously available now; use Sentry.ErrorBoundary if initialised
const SentryEB = window.GQ?.observability?.ErrorBoundary;
root.render(
  SentryEB
    ? <SentryEB fallback={ErrorFallback}><App/></SentryEB>
    : <App/>
);
